// PreviewTester/utils/valueGenerator.js

// Get inverse condition - handles combined conditions properly with De Morgan's Law
export const getInverseCondition = (condition) => {
    console.log('[BUG_ELSE_DISPLAY] 🔍 getInverseCondition INPUT:', JSON.stringify(condition));

    // Normalize condition: replace 'or' with '||' and 'and' with '&&' for consistent parsing
    const normalized = condition
        .replace(/\s+or\s+/g, ' || ')
        .replace(/\s+and\s+/g, ' && ');

    // Handle AND conditions: !(A && B) = !A || !B
    if (normalized.includes(' && ')) {
        const parts = normalized.split(' && ');
        const inverses = parts.map(p => getInverseCondition(p.trim()));
        const result = inverses.join(' || ');
        console.log('[BUG_ELSE_DISPLAY] 🔍 getInverseCondition AND result:', JSON.stringify(result));
        return result;
    }

    // Handle OR conditions: !(A || B) = !A && !B
    if (normalized.includes(' || ')) {
        const parts = normalized.split(' || ');
        const inverses = parts.map(p => getInverseCondition(p.trim()));
        const result = inverses.join(' && ');
        console.log('[BUG_ELSE_DISPLAY] 🔍 getInverseCondition OR result:', JSON.stringify(result));
        return result;
    }

    // Handle simple operators
    let result = normalized;
    if (normalized.includes('>=')) {
        result = normalized.replace('>=', '<');
    } else if (normalized.includes('<=')) {
        result = normalized.replace('<=', '>');
    } else if (normalized.includes('>')) {
        result = normalized.replace('>', '<=');
    } else if (normalized.includes('<')) {
        result = normalized.replace('<', '>=');
    } else if (normalized.includes('===')) {
        result = normalized.replace('===', '!==');
    } else if (normalized.includes('==')) {
        result = normalized.replace('==', '!=');
    } else if (normalized.includes('!==')) {
        result = normalized.replace('!==', '===');
    } else if (normalized.includes('!=')) {
        result = normalized.replace('!=', '==');
    }

    console.log('[BUG_ELSE_DISPLAY] 🔍 getInverseCondition simple result:', JSON.stringify(result));
    return result;
};

// Build else condition from all if/elif conditions
export const buildElseCondition = (conditions) => {
    console.log('[BUG_ELSE_DISPLAY] 🔍 buildElseCondition INPUT:', JSON.stringify(conditions));

    if (!conditions || conditions.length === 0) {
        console.log('[BUG_ELSE_TRUE] ❌ No conditions provided, returning "true"');
        return 'true';
    }

    // Check if all conditions are boolean comparisons
    const allBoolean = conditions.every(cond => {
        return cond.includes('== true') || cond.includes('== false') ||
            cond.includes('!= true') || cond.includes('!= false');
    });

    if (allBoolean) {
        console.log('[BUG_ELSE_DISPLAY] 🔍 All boolean conditions detected');
        const fields = conditions.map(cond => {
            const match = cond.match(/^[a-zA-Z_.]+/);
            return match ? match[0] : cond;
        });
        const uniqueFields = [...new Set(fields)];

        if (uniqueFields.length === 1) {
            const result = `${uniqueFields[0]} != true && ${uniqueFields[0]} != false`;
            console.log('[BUG_ELSE_DISPLAY] 🔍 Boolean else result:', JSON.stringify(result));
            return result;
        }
    }

    // For a single condition, just invert it
    if (conditions.length === 1) {
        const inverse = getInverseCondition(conditions[0]);
        console.log('[BUG_ELSE_DISPLAY] 🔍 Single condition else result:', JSON.stringify(inverse));
        return inverse;
    }

    // For multiple conditions (elif chain), invert each and join with &&
    // Add parentheses around OR conditions for correct operator precedence
    const inverseConditions = conditions.map(cond => {
        const inverse = getInverseCondition(cond);
        // If the inverse contains ||, wrap it in parentheses
        if (inverse.includes(' || ')) {
            return `(${inverse})`;
        }
        return inverse;
    });
    const result = inverseConditions.join(' && ');
    console.log('[BUG_ELSE_DISPLAY] 🔍 Multi-condition else result:', JSON.stringify(result));
    return result;
};

// Create data update helper
const createDataUpdate = (fieldParts, fieldName, val) => {
    const update = {};
    let ref = update;

    let startIndex = 0;
    if (fieldParts.length > 0 && fieldParts[0] === 'response_data') {
        startIndex = 1;
    }

    if (startIndex >= fieldParts.length) {
        update[fieldName] = val;
        return update;
    }

    for (let i = startIndex; i < fieldParts.length - 1; i++) {
        ref[fieldParts[i]] = {};
        ref = ref[fieldParts[i]];
    }
    ref[fieldName] = val;
    return update;
};

// Check if a value satisfies a condition
const satisfiesCondition = (value, condition) => {
    const operators = ['>=', '<=', '>', '<', '===', '==', '!==', '!=', 'in', 'not in'];
    let field = null;
    let operator = null;
    let compareValue = null;

    for (const op of operators) {
        if (condition.includes(op)) {
            const parts = condition.split(op);
            field = parts[0].trim();
            compareValue = parts[1].trim();
            operator = op;
            break;
        }
    }

    if (!field || !operator) return false;

    compareValue = compareValue.replace(/^['"]|['"]$/g, '');
    const isNumberCompare = !isNaN(compareValue);

    if (isNumberCompare) {
        const numCompare = parseInt(compareValue);
        const numValue = parseInt(value);
        switch (operator) {
            case '>': return numValue > numCompare;
            case '>=': return numValue >= numCompare;
            case '<': return numValue < numCompare;
            case '<=': return numValue <= numCompare;
            case '==': return numValue === numCompare;
            case '===': return numValue === numCompare;
            case '!=': return numValue !== numCompare;
            case '!==': return numValue !== numCompare;
            default: return false;
        }
    } else {
        const strValue = String(value);
        switch (operator) {
            case '==': return strValue === compareValue;
            case '===': return strValue === compareValue;
            case '!=': return strValue !== compareValue;
            case '!==': return strValue !== compareValue;
            case 'in': return compareValue.includes(strValue);
            case 'not in': return !compareValue.includes(strValue);
            default: return false;
        }
    }
};

// Get simple field name without response_data prefix
const getSimpleFieldName = (field) => {
    const parts = field.split('.');
    return parts[parts.length - 1];
};

// Parse a complex condition to extract all fields
const parseCondition = (condition) => {
    const parseSingleCondition = (cond) => {
        const trimmed = cond.trim();
        const operators = ['>=', '<=', '>', '<', '===', '==', '!==', '!=', 'in', 'not in'];
        for (const op of operators) {
            if (trimmed.includes(op)) {
                const subParts = trimmed.split(op);
                return {
                    field: subParts[0].trim(),
                    operator: op,
                    compareValue: subParts[1].trim()
                };
            }
        }
        return {
            field: trimmed,
            operator: null,
            compareValue: null
        };
    };

    // Check for AND conditions (both && and 'and')
    const andMatches = [];
    let inQuotes = false;
    let currentPart = '';
    let quoteChar = '';

    for (let i = 0; i < condition.length; i++) {
        const char = condition[i];
        if ((char === '"' || char === "'") && (i === 0 || condition[i - 1] !== '\\')) {
            if (!inQuotes) {
                inQuotes = true;
                quoteChar = char;
                currentPart += char;
            } else if (char === quoteChar) {
                inQuotes = false;
                currentPart += char;
            } else {
                currentPart += char;
            }
            continue;
        }

        if (!inQuotes && char === ' ' && condition.substring(i, i + 5) === ' and ') {
            andMatches.push(currentPart.trim());
            currentPart = '';
            i += 4;
            continue;
        }

        if (!inQuotes && char === ' ' && condition.substring(i, i + 4) === ' && ') {
            andMatches.push(currentPart.trim());
            currentPart = '';
            i += 3;
            continue;
        }

        currentPart += char;
    }

    if (currentPart.trim()) {
        andMatches.push(currentPart.trim());
    }

    if (andMatches.length > 1) {
        const parsedParts = andMatches.map(p => parseSingleCondition(p));
        return {
            type: 'and',
            parts: parsedParts,
            field: parsedParts[0].field,
            operator: parsedParts[0].operator,
            compareValue: parsedParts[0].compareValue
        };
    }

    // Check for OR conditions (both || and 'or')
    const orMatches = [];
    currentPart = '';
    inQuotes = false;
    quoteChar = '';

    for (let i = 0; i < condition.length; i++) {
        const char = condition[i];
        if ((char === '"' || char === "'") && (i === 0 || condition[i - 1] !== '\\')) {
            if (!inQuotes) {
                inQuotes = true;
                quoteChar = char;
                currentPart += char;
            } else if (char === quoteChar) {
                inQuotes = false;
                currentPart += char;
            } else {
                currentPart += char;
            }
            continue;
        }

        if (!inQuotes && char === ' ' && condition.substring(i, i + 4) === ' or ') {
            orMatches.push(currentPart.trim());
            currentPart = '';
            i += 3;
            continue;
        }

        if (!inQuotes && char === ' ' && condition.substring(i, i + 4) === ' || ') {
            orMatches.push(currentPart.trim());
            currentPart = '';
            i += 3;
            continue;
        }

        currentPart += char;
    }

    if (currentPart.trim()) {
        orMatches.push(currentPart.trim());
    }

    if (orMatches.length > 1) {
        const parsedParts = orMatches.map(p => parseSingleCondition(p));
        return {
            type: 'or',
            parts: parsedParts,
            field: parsedParts[0].field,
            operator: parsedParts[0].operator,
            compareValue: parsedParts[0].compareValue
        };
    }

    // Simple condition
    const operators = ['>=', '<=', '>', '<', '===', '==', '!==', '!=', 'in', 'not in'];
    for (const op of operators) {
        if (condition.includes(op)) {
            const parts = condition.split(op);
            return {
                type: 'simple',
                parts: [{
                    field: parts[0].trim(),
                    operator: op,
                    compareValue: parts[1].trim()
                }],
                field: parts[0].trim(),
                operator: op,
                compareValue: parts[1].trim()
            };
        }
    }

    return {
        type: 'truthy',
        parts: [{
            field: condition.trim(),
            operator: null,
            compareValue: null
        }],
        field: condition.trim(),
        operator: null,
        compareValue: null
    };
};

// Generate a clean label for a condition
const generateCleanLabel = (fieldName, value, conditionDetails = null) => {
    let label = `${fieldName}: ${value}`;

    if (conditionDetails) {
        const { operator, compareValue, isElse, allValues, isTruthy, conditionType, parts } = conditionDetails;
        let suffix = '';

        if (isTruthy) {
            suffix = value ? 'truthy' : 'falsy';
        } else if (isElse && allValues && allValues.length > 0) {
            const values = allValues.map(v => `"${v}"`).join(', ');
            suffix = `not in [${values}]`;
        } else if (isElse) {
            if (operator === '>' || operator === '>=') {
                suffix = `<= ${compareValue}`;
            } else if (operator === '<' || operator === '<=') {
                suffix = `>= ${compareValue}`;
            } else if (operator === '==') {
                suffix = `!= ${compareValue}`;
            } else if (operator === '!=') {
                suffix = `== ${compareValue}`;
            } else {
                suffix = `not ${compareValue}`;
            }
        } else {
            suffix = `${operator} ${compareValue}`;
        }

        if (conditionType === 'and' || conditionType === 'or') {
            const partsLabels = parts.map(p => {
                const cleanCompare = p.compareValue ? p.compareValue.replace(/^['"]|['"]$/g, '') : '';
                const simpleField = getSimpleFieldName(p.field);
                return `${simpleField}: ${cleanCompare}`;
            }).join(conditionType === 'and' ? ' AND ' : ' OR ');
            label = partsLabels;
        } else {
            label = `${fieldName}: ${value} (${suffix})`;
        }
    }

    return label;
};

// Generate a random string of specified length
const generateRandomString = (length = 3) => {
    const characters = 'abcdefghijklmnopqrstuvwxyz';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
};

// Generate a random string that's not in the forbidden list
const generateUniqueRandomString = (forbiddenValues, length = 3) => {
    let attempts = 0;
    const maxAttempts = 100;
    let result;

    do {
        result = generateRandomString(length);
        attempts++;
        if (attempts > maxAttempts) {
            length++;
            attempts = 0;
        }
    } while (forbiddenValues.includes(result) && attempts < maxAttempts * 2);

    return result;
};

// Generate a random number that's not in the forbidden list
const generateUniqueRandomNumber = (forbiddenValues, min = 0, max = 100) => {
    let attempts = 0;
    const maxAttempts = 100;
    let result;

    do {
        result = Math.floor(Math.random() * (max - min + 1)) + min;
        attempts++;
        if (attempts > maxAttempts) {
            max += 10;
            attempts = 0;
        }
    } while (forbiddenValues.includes(String(result)) && attempts < maxAttempts * 2);

    return result;
};

// Get current value from data
const getCurrentValueFromData = (fieldParts, data) => {
    let dataRef = data;
    for (const part of fieldParts) {
        if (dataRef && typeof dataRef === 'object' && part in dataRef) {
            dataRef = dataRef[part];
        } else {
            dataRef = undefined;
            break;
        }
    }
    return dataRef;
};

// Extract all values from a condition
const extractAllConditionValues = (condition) => {
    const values = [];
    const operators = ['>=', '<=', '>', '<', '===', '==', '!==', '!=', 'in', 'not in'];

    const normalized = condition
        .replace(/\s+and\s+/g, ' && ')
        .replace(/\s+or\s+/g, ' || ');

    if (normalized.includes(' && ')) {
        const parts = normalized.split(' && ');
        parts.forEach(p => {
            const trimmed = p.trim();
            for (const op of operators) {
                if (trimmed.includes(op)) {
                    const subParts = trimmed.split(op);
                    const val = subParts[1].trim().replace(/^['"]|['"]$/g, '');
                    values.push(val);
                    break;
                }
            }
        });
        return values;
    }

    if (normalized.includes(' || ')) {
        const parts = normalized.split(' || ');
        parts.forEach(p => {
            const trimmed = p.trim();
            for (const op of operators) {
                if (trimmed.includes(op)) {
                    const subParts = trimmed.split(op);
                    const val = subParts[1].trim().replace(/^['"]|['"]$/g, '');
                    values.push(val);
                    break;
                }
            }
        });
        return values;
    }

    for (const op of operators) {
        if (condition.includes(op)) {
            const parts = condition.split(op);
            const val = parts[1].trim().replace(/^['"]|['"]$/g, '');
            values.push(val);
            break;
        }
    }

    return values;
};

// Check if a condition involves boolean values
const isBooleanCondition = (condition) => {
    return condition.includes('== true') || condition.includes('== false') ||
        condition.includes('!= true') || condition.includes('!= false');
};

// Extract the first field from a condition
const extractFirstField = (condition) => {
    const match = condition.match(/^[a-zA-Z_.]+/);
    return match ? match[0] : null;
};

// Generate a value that satisfies the condition
const generateValueForCondition = (condition, currentData, isElse = false) => {
    if (condition === 'true') {
        return {
            label: 'true: null',
            value: null,
            fieldPath: 'true',
            dataUpdate: {},
            updateField: 'true'
        };
    }

    const parsed = parseCondition(condition);
    if (!parsed || !parsed.field) return null;

    // For AND/OR conditions
    if (parsed.type === 'and' || parsed.type === 'or') {
        const allParts = parsed.parts;
        const generatedParts = allParts.map(p => {
            const subCondition = `${p.field} ${p.operator} ${p.compareValue}`;
            return generateValueForCondition(subCondition, currentData, isElse);
        });

        let combinedDataUpdate = {};
        generatedParts.forEach((gen) => {
            if (gen && gen.dataUpdate) combinedDataUpdate = { ...combinedDataUpdate, ...gen.dataUpdate };
        });

        const firstPart = allParts[0];
        const fieldParts = firstPart.field.split('.');
        const fieldName = fieldParts[fieldParts.length - 1];
        const firstValue = generatedParts[0] ? generatedParts[0].value : null;

        const conditionDetails = {
            conditionType: parsed.type,
            parts: allParts.map(p => ({
                field: p.field,
                compareValue: p.compareValue ? p.compareValue.replace(/^['"]|['"]$/g, '') : '',
                operator: p.operator
            })),
            isElse, allValues: [], isTruthy: false
        };

        const label = generateCleanLabel(fieldName, firstValue, conditionDetails);
        return { label, value: firstValue, fieldPath: firstPart.field, dataUpdate: combinedDataUpdate, updateField: fieldName };
    }

    // Handle truthy check
    if (parsed.type === 'truthy') {
        const fieldParts = parsed.field.split('.');
        const fieldName = fieldParts[fieldParts.length - 1];
        const currentVal = getCurrentValueFromData(fieldParts, currentData);

        if (isElse) {
            const value = null;
            const label = generateCleanLabel(fieldName, value, { isTruthy: true });
            const updateFieldParts = fieldParts.slice(1);
            const dataUpdate = createDataUpdate(updateFieldParts, fieldName, value);
            return { label, value, fieldPath: parsed.field, dataUpdate, updateField: updateFieldParts.join('.') || fieldName, isTruthy: true };
        } else {
            const value = currentVal !== undefined && currentVal !== null ? currentVal : 'has_value';
            const updateFieldParts = fieldParts.slice(1);
            const dataUpdate = createDataUpdate(updateFieldParts, fieldName, value);
            const label = generateCleanLabel(fieldName, value, { isTruthy: true });
            return { label, value, fieldPath: parsed.field, dataUpdate, updateField: fieldParts.slice(1).join('.') || fieldName, isTruthy: true };
        }
    }

    // Simple condition
    const compareValueClean = parsed.compareValue.replace(/^['"]|['"]$/g, '');
    const fieldParts = parsed.field.split('.');
    const fieldName = fieldParts[fieldParts.length - 1];
    const isNumberCompare = !isNaN(compareValueClean);
    const isBoolean = (compareValueClean === 'true' || compareValueClean === 'false');
    const allConditionValues = extractAllConditionValues(condition);

    let value;
    const conditionDetails = {
        operator: parsed.operator,
        compareValue: compareValueClean,
        isElse, allValues: [], isTruthy: false,
        conditionType: 'simple',
        parts: [{ field: parsed.field, compareValue: compareValueClean, operator: parsed.operator }]
    };

    if (isBoolean) {
        value = (isElse && parsed.operator !== '!=' && parsed.operator !== '!==') ? null : compareValueClean;
    } else if (isNumberCompare) {
        const numCompare = parseInt(compareValueClean);
        if (isElse) {
            if (parsed.operator === '>' || parsed.operator === '>=') value = String(Math.max(0, numCompare - 1));
            else if (parsed.operator === '<' || parsed.operator === '<=') value = String(numCompare + 1);
            else if (parsed.operator === '==' || parsed.operator === '===') value = String(generateUniqueRandomNumber(allConditionValues.map(String), 0, 100));
            else value = String(numCompare);
        } else {
            switch (parsed.operator) {
                case '>': value = String(numCompare + 1); break;
                case '>=': value = String(numCompare); break;
                case '<': value = String(Math.max(0, numCompare - 1)); break;
                case '<=': value = String(numCompare); break;
                case '==': case '===': value = String(numCompare); break;
                case '!=': case '!==': value = String(generateUniqueRandomNumber(allConditionValues.map(String), 0, 100)); break;
                default: value = String(numCompare);
            }
        }
    } else {
        if (isElse) {
            if (parsed.operator === '==' || parsed.operator === '===') {
                value = generateUniqueRandomString(allConditionValues, 3);
                conditionDetails.allValues = allConditionValues;
            } else if (parsed.operator === '!=' || parsed.operator === '!==') {
                value = compareValueClean;
            } else {
                value = generateUniqueRandomString(allConditionValues, 3);
            }
        } else {
            switch (parsed.operator) {
                case '==': case '===': value = compareValueClean; break;
                case '!=': case '!==': value = generateUniqueRandomString(allConditionValues, 3); break;
                default: value = compareValueClean;
            }
        }
    }

    const label = generateCleanLabel(fieldName, value, conditionDetails);
    let updateFieldParts = fieldParts;
    if (fieldParts.length > 0 && fieldParts[0] === 'response_data') updateFieldParts = fieldParts.slice(1);
    const dataUpdate = createDataUpdate(updateFieldParts, fieldName, value);

    return { label, value, fieldPath: parsed.field, dataUpdate, updateField: updateFieldParts.length > 0 ? updateFieldParts.join('.') : fieldName };
};

// Generate a single "else" value
// IMPORTANT: This function receives the ALREADY-INVERTED else condition
// It should generate a value that SATISFIES this condition, NOT invert it again
export const generateElseValue = (condition, currentData) => {
    console.log('[BUG_ELSE_VALUE] 🔍 generateElseValue INPUT:', JSON.stringify({ condition }));

    if (condition === 'true') {
        return { label: 'true: null', value: null, fieldPath: 'true', dataUpdate: {}, updateField: 'true' };
    }

    // Normalize 'or'/'and' to '||'/'&&'
    const normalized = condition
        .replace(/\s+or\s+/g, ' || ')
        .replace(/\s+and\s+/g, ' && ');

    // Handle OR conditions - take first part that gives us a value
    if (normalized.includes(' || ')) {
        const parts = normalized.split(' || ');
        // For OR, we just need ONE part to be true
        // Take the first part and generate a value that satisfies it
        return generateSatisfyingValue(parts[0].trim(), currentData);
    }

    // Handle AND conditions - take first part
    if (normalized.includes(' && ')) {
        const parts = normalized.split(' && ').map(p => p.trim());
        const hasBoolean = parts.some(p => p.includes('== true') || p.includes('== false') || p.includes('!= true') || p.includes('!= false'));
        if (hasBoolean) {
            const firstField = extractFirstField(parts[0]);
            if (firstField) {
                const fieldParts = firstField.split('.');
                const simpleFieldName = fieldParts[fieldParts.length - 1];
                const value = null;
                const label = `${simpleFieldName}: null`;
                const dataUpdate = createDataUpdate(fieldParts.slice(1), simpleFieldName, value);
                return { label, value, fieldPath: firstField, dataUpdate, updateField: fieldParts.length > 0 ? fieldParts.join('.') : simpleFieldName };
            }
        }
        // For AND, take first part and satisfy it
        return generateSatisfyingValue(parts[0], currentData);
    }

    // Simple condition - generate a value that SATISFIES it
    return generateSatisfyingValue(normalized, currentData);
};

// Generate a value that SATISFIES a simple condition (does NOT invert)
const generateSatisfyingValue = (condition, currentData) => {
    console.log('[BUG_ELSE_VALUE] 🔍 generateSatisfyingValue:', JSON.stringify({ condition }));

    let fieldName, compareValue, fieldParts, simpleFieldName, numCompare, value, label, dataUpdate;

    if (condition.includes('>=')) {
        [fieldName, compareValue] = condition.split('>=');
        fieldParts = fieldName.trim().split('.');
        simpleFieldName = fieldParts[fieldParts.length - 1];
        numCompare = parseInt(compareValue.trim());
        value = String(numCompare);  // Satisfies >=
        label = `${simpleFieldName}: ${value} (>= ${compareValue.trim()})`;
    } else if (condition.includes('<=')) {
        [fieldName, compareValue] = condition.split('<=');
        fieldParts = fieldName.trim().split('.');
        simpleFieldName = fieldParts[fieldParts.length - 1];
        numCompare = parseInt(compareValue.trim());
        value = String(numCompare);  // Satisfies <=
        label = `${simpleFieldName}: ${value} (<= ${compareValue.trim()})`;
    } else if (condition.includes('>')) {
        [fieldName, compareValue] = condition.split('>');
        fieldParts = fieldName.trim().split('.');
        simpleFieldName = fieldParts[fieldParts.length - 1];
        numCompare = parseInt(compareValue.trim());
        value = String(numCompare + 1);  // Satisfies >
        label = `${simpleFieldName}: ${value} (> ${compareValue.trim()})`;
    } else if (condition.includes('<')) {
        [fieldName, compareValue] = condition.split('<');
        fieldParts = fieldName.trim().split('.');
        simpleFieldName = fieldParts[fieldParts.length - 1];
        numCompare = parseInt(compareValue.trim());
        value = String(Math.max(0, numCompare - 1));  // Satisfies <
        label = `${simpleFieldName}: ${value} (< ${compareValue.trim()})`;
    } else if (condition.includes('===') || condition.includes('==')) {
        const operator = condition.includes('===') ? '===' : '==';
        [fieldName, compareValue] = condition.split(operator);
        fieldParts = fieldName.trim().split('.');
        simpleFieldName = fieldParts[fieldParts.length - 1];
        compareValue = compareValue.trim().replace(/^['"]|['"]$/g, '');

        if (compareValue === 'true' || compareValue === 'false') {
            value = null;
            label = `${simpleFieldName}: null`;
        } else {
            value = compareValue;  // Satisfies ==
            label = `${simpleFieldName}: "${value}" (== ${compareValue})`;
        }
    } else if (condition.includes('!==') || condition.includes('!=')) {
        const operator = condition.includes('!==') ? '!==' : '!=';
        [fieldName, compareValue] = condition.split(operator);
        fieldParts = fieldName.trim().split('.');
        simpleFieldName = fieldParts[fieldParts.length - 1];
        compareValue = compareValue.trim().replace(/^['"]|['"]$/g, '');
        value = generateUniqueRandomString([compareValue], 3);  // Satisfies !=
        label = `${simpleFieldName}: "${value}" (!= ${compareValue})`;
    } else {
        // Truthy check - any non-null value
        fieldParts = condition.trim().split('.');
        simpleFieldName = fieldParts[fieldParts.length - 1];
        value = 'has_value';
        label = `${simpleFieldName}: ${value}`;
    }

    dataUpdate = createDataUpdate(fieldParts.slice(1), simpleFieldName, value);
    console.log('[BUG_ELSE_VALUE] 🔍 Result:', JSON.stringify({ label, value }));
    return { label, value, fieldPath: fieldName.trim(), dataUpdate, updateField: fieldParts.length > 0 ? fieldParts.join('.') : simpleFieldName };
};

// Generate test values for any condition
export const generateTestValues = (condition, currentData, isElse = false) => {
    const values = [];

    if (condition === 'true') {
        values.push({ label: 'true: null', value: null, fieldPath: 'true', dataUpdate: {}, isActive: false, isElse: true });
        return values;
    }

    const parsed = parseCondition(condition);
    if (!parsed || !parsed.field) return values;

    // Truthy check
    if (parsed.type === 'truthy') {
        const fieldParts = parsed.field.split('.');
        const fieldName = fieldParts[fieldParts.length - 1];

        if (isElse) {
            const value = null;
            const dataUpdate = createDataUpdate(fieldParts.slice(1), fieldName, value);
            values.push({ label: `${fieldName}: null`, value, fieldPath: parsed.field, dataUpdate, isActive: false, isElse: true });
        } else {
            const currentVal = getCurrentValueFromData(fieldParts, currentData);
            const value = currentVal !== undefined && currentVal !== null ? currentVal : 'has_value';
            const dataUpdate = createDataUpdate(fieldParts.slice(1), fieldName, value);
            values.push({ label: `${fieldName}: ${value}`, value, fieldPath: parsed.field, dataUpdate, isActive: true, isElse: false });
        }
        return values;
    }

    // AND/OR conditions
    if (parsed.type === 'and' || parsed.type === 'or') {
        const allParts = parsed.parts;

        if (isElse) {
            if (parsed.type === 'and') {
                const firstPart = allParts[0];
                const subCondition = `${firstPart.field} ${firstPart.operator} ${firstPart.compareValue}`;
                const generatedValue = generateElseValue(subCondition, currentData);
                if (generatedValue) {
                    const fieldParts = firstPart.field.split('.');
                    const currentValue = getCurrentValueFromData(fieldParts, currentData);
                    const isActive = String(generatedValue.value) === String(currentValue);
                    values.push({ ...generatedValue, isActive, isElse: true });
                }
            } else {
                const allCompareValues = [];
                allParts.forEach(p => {
                    const cleanCompare = p.compareValue ? p.compareValue.replace(/^['"]|['"]$/g, '') : '';
                    if (cleanCompare) allCompareValues.push(cleanCompare);
                });

                if (allParts.length > 0) {
                    const firstField = allParts[0].field;
                    const fieldParts = firstField.split('.');
                    const simpleFieldName = fieldParts[fieldParts.length - 1];
                    const value = allCompareValues.length > 0 ? generateUniqueRandomString(allCompareValues, 3) : 'other';
                    const label = `${simpleFieldName}: "${value}"`;
                    const dataUpdate = createDataUpdate(fieldParts.slice(1), simpleFieldName, value);
                    const currentValue = getCurrentValueFromData(fieldParts, currentData);
                    const isActive = String(value) === String(currentValue);
                    values.push({ label, value, fieldPath: firstField, dataUpdate, isActive, isElse: true });
                }
            }
        } else {
            const firstPart = allParts[0];
            const subCondition = `${firstPart.field} ${firstPart.operator} ${firstPart.compareValue}`;
            const subValues = generateTestValues(subCondition, currentData, false);
            if (subValues.length > 0) {
                const subValue = subValues[0];
                const partsLabels = allParts.map(p => {
                    const simpleField = getSimpleFieldName(p.field);
                    const cleanCompare = p.compareValue ? p.compareValue.replace(/^['"]|['"]$/g, '') : '';
                    return `${simpleField}: ${cleanCompare}`;
                }).join(parsed.type === 'and' ? ' AND ' : ' OR ');
                values.push({ ...subValue, label: partsLabels, isElse: false });
            }
        }
        return values;
    }

    // Simple condition
    const { field, operator, compareValue } = parsed;
    const compareValueClean = compareValue ? compareValue.replace(/^['"]|['"]$/g, '') : '';
    const fieldParts = field.split('.');
    const fieldName = fieldParts[fieldParts.length - 1];
    const currentValue = getCurrentValueFromData(fieldParts, currentData);

    if (isElse) {
        const generatedValue = generateElseValue(condition, currentData);
        if (generatedValue) {
            const isActive = String(generatedValue.value) === String(currentValue);
            values.push({ ...generatedValue, isActive, isElse: true });
        }
        return values;
    }

    let valueToUse = null, dataUpdate = null, generatedValue = null;

    if (currentValue !== undefined && currentValue !== null) {
        if (satisfiesCondition(currentValue, condition)) {
            valueToUse = String(currentValue);
            let updateFieldParts = fieldParts;
            if (fieldParts.length > 0 && fieldParts[0] === 'response_data') updateFieldParts = fieldParts.slice(1);
            dataUpdate = createDataUpdate(updateFieldParts, fieldName, valueToUse);
        } else {
            generatedValue = generateValueForCondition(condition, currentData, false);
            if (generatedValue) { valueToUse = generatedValue.value; dataUpdate = generatedValue.dataUpdate; }
        }
    } else {
        generatedValue = generateValueForCondition(condition, currentData, false);
        if (generatedValue) { valueToUse = generatedValue.value; dataUpdate = generatedValue.dataUpdate; }
    }

    if (valueToUse === null || dataUpdate === null) {
        const fallbackValue = generateValueForCondition(condition, currentData, false);
        if (fallbackValue) { valueToUse = fallbackValue.value; dataUpdate = fallbackValue.dataUpdate; }
    }

    if (valueToUse !== null && dataUpdate !== null) {
        const isActive = String(valueToUse) === String(currentValue);
        const label = generatedValue?.label || generateCleanLabel(fieldName, valueToUse, { operator, compareValue: compareValueClean, isElse: false, isTruthy: false });
        values.push({ label, value: valueToUse, fieldPath: field, dataUpdate, isActive, isElse: false });
    }

    return values;
};
